import { useSyncExternalStore } from "react"

/** Fallbacks for when the server's price test cannot be asked; the server owns both numbers (ReportPriceTier, ReportPayment). */
export const REPORT_PRICE_EUR = 12
export const FALLBACK_DISPLAY_PRICE_EUR = 36

const STORAGE_KEY = "hi-report-price-v5"

/** A plan on the unlock panel. The server decides whether a visitor is shown the plans and what each costs. */
export interface PlanOffer {
    plan: "week" | "year" | "lifetime"
    amount_eur: number
    interval: "week" | "year" | null
}

interface ReportPrices {
    display: number
    charged: number
    plans: PlanOffer[]
    singleUnlock: number | null
}

const NO_PLANS: PlanOffer[] = []

const FALLBACK: ReportPrices = { display: FALLBACK_DISPLAY_PRICE_EUR, charged: REPORT_PRICE_EUR, plans: NO_PLANS, singleUnlock: null }

const isPlanOffer = (value: any): value is PlanOffer =>
    ["week", "year", "lifetime"].includes(value?.plan) && typeof value?.amount_eur === "number" && value.amount_eur > 0

const plansFrom = (value: unknown): PlanOffer[] =>
    Array.isArray(value) && value.length > 0 && value.every(isPlanOffer) ? value : NO_PLANS

const singleUnlockFrom = (value: any): number | null => isPrice(value?.amount_eur) ? value.amount_eur : null

const listeners = new Set<() => void>()
let current: ReportPrices | null = null

const isPrice = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value > 0

function readStored(): ReportPrices | null {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null")
        return parsed && isPrice(parsed.display) && isPrice(parsed.charged)
            ? { display: parsed.display, charged: parsed.charged, plans: plansFrom(parsed.plans), singleUnlock: isPrice(parsed.singleUnlock) ? parsed.singleUnlock : null }
            : null
    } catch {
        return null
    }
}

function prices(): ReportPrices {
    if (!current) current = readStored()
    return current ?? FALLBACK
}

/** What the visitor is shown. */
export const displayPriceEur = () => prices().display

/** What the visitor is charged. */
export const chargedPriceEur = () => prices().charged

/** The plans this visitor is offered on the unlock panel, none for most. */
export const planOffers = () => prices().plans

/** What one home costs to unlock, or null when this visitor isn't offered it. */
export const singleUnlockPriceEur = () => prices().singleUnlock

export function formatReportPrice(euro: number = displayPriceEur()): string {
    return `€${Number.isInteger(euro) ? euro : euro.toFixed(2)}`
}

/** Re-renders a component when the visitor's price arrives. */
export function useReportPrices(): ReportPrices {
    const subscribe = (listener: () => void) => {
        listeners.add(listener)
        return () => { listeners.delete(listener) }
    }
    const display = useSyncExternalStore(subscribe, displayPriceEur)
    const charged = useSyncExternalStore(subscribe, chargedPriceEur)
    const plans = useSyncExternalStore(subscribe, planOffers)
    const singleUnlock = useSyncExternalStore(subscribe, singleUnlockPriceEur)
    return { display, charged, plans, singleUnlock }
}

let inFlight: Promise<void> | null = null

/** Asks for this visitor's price once and keeps it; a failure leaves the fallback and is retried on the next page. */
export function ensureReportPrices(fetchPrices: () => Promise<{ display_price_eur?: number, charged_price_eur?: number, plans?: PlanOffer[], single_unlock?: { amount_eur: number } | null }>): Promise<void> {
    if (readStored()) return Promise.resolve()
    if (inFlight) return inFlight
    inFlight = Promise.resolve().then(fetchPrices)
        .then((response) => {
            if (!isPrice(response?.display_price_eur) || !isPrice(response?.charged_price_eur)) return
            current = { display: response.display_price_eur, charged: response.charged_price_eur, plans: plansFrom(response.plans), singleUnlock: singleUnlockFrom(response.single_unlock) }
            try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current)) } catch { /* storage is off: the price still holds for this page */ }
            listeners.forEach((listener) => listener())
        })
        .catch(() => undefined)
        .finally(() => { inFlight = null })
    return inFlight
}

export function resetReportPrices() {
    current = null
    inFlight = null
    try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* nothing to forget */ }
    listeners.forEach((listener) => listener())
}
